<script setup lang="ts">
import userImage from "@/assets/icons/profile.svg";
import { useUserStore } from "@/stores/userStore";
import { useI18n } from "vue-i18n";
import router from "@/router";
import { onMounted, ref } from "vue";
import { ROUTE_NAMES } from "@/constants/routeNames";
import AuthModal from "@/views/Auth/components/AuthModal.vue";
import SubscriptionModal from "@/views/Auth/components/SubscriptionModal.vue";
import BackButton from "@/components/buttons/BackButton.vue";
import { useSafeBack } from "@/utils/useSafeBack";
import NotificationComponent from "@/components/notifications/NotificationComponent.vue";
import { PREMIUM_CALCULATORS, PremiumCalculator } from "@/types/premiumTypes";
import ActiveSubscriptionCard from "@/views/Auth/components/ActiveSubscriptionCard.vue";
import SubscriptionCard from "@/views/Auth/components/SubscriptionCard.vue";
import { useRoute } from "vue-router";
import FamilyTable from "@/views/Auth/components/FamilyTable.vue";
import DestinyMatrixPurchadesDatesTable from "@/views/Auth/components/DestinyMatrixPurchadesDatesTable.vue";
import OutlineButton from "@/components/buttons/OutlineButton.vue";

const userStore = useUserStore();
const { t } = useI18n();
const { safeBack } = useSafeBack();
const route = useRoute();

const ALL_PREMIUM_CALCULATORS = Object.values(PREMIUM_CALCULATORS);
const defaultSelectedSubscription = ref<PremiumCalculator>();
const showSubscribeModal = ref(false);
const showAuthModal = ref(false);
const loadingState = ref<Record<string, boolean>>({});

onMounted(() => {
  if (userStore.user === null) {
    showAuthModal.value = true;
    return;
  }
  // fetch new user info when opening this page
  userStore.updateUserInfo(true);

  const calculator = route.query.openCalculator;
  if (calculator) {
    defaultSelectedSubscription.value = calculator as PremiumCalculator;
    showSubscribeModal.value = true;
    router.replace({ query: { ...route.query, openCalculator: undefined } });
  }
});

async function logout() {
  try {
    loadingState.value["logout"] = true;
    await userStore.logout();
    await router.replace({ name: ROUTE_NAMES.HOME });
  } catch (e) {
    console.error(e);
  } finally {
    loadingState.value["logout"] = false;
  }
}

function openSubscriptionModal(subscription: PremiumCalculator) {
  defaultSelectedSubscription.value = subscription;
  showSubscribeModal.value = true;
}

async function handleCancelSubscription(subscription: PremiumCalculator) {
  try {
    loadingState.value["cancel-subscription"] = true;
    await userStore.cancelSubscription(subscription);
  } catch (e) {
    console.error(e);
  } finally {
    loadingState.value["cancel-subscription"] = false;
  }
}

function handleCloseAuthModal() {
  if (userStore.user !== null) {
    showAuthModal.value = false;
  } else {
    router.replace({ name: ROUTE_NAMES.HOME });
  }
}

function handleBack() {
  safeBack(ROUTE_NAMES.HOME);
}

const isLoading = (id: string): boolean => {
  return loadingState.value[id] ?? false;
};
</script>

<template>
  <div
    class="w-full h-full max-md:bg-secondary px-4 pt-6 md:pt-2 pb-2 overflow-y-auto no-scrollbar"
  >
    <!-- BACK BUTTON -->
    <div class="md:hidden flex justify-between items-center mb-6">
      <div class="w-[74px]">
        <BackButton @clicked="handleBack" />
      </div>
      <p class="text-brown font-semibold text-2xl">{{ t("profile.title") }}</p>
      <OutlineButton
        class="h-8 w-[74px]"
        :isLoading="isLoading('logout')"
        :btn-text="t('buttons.auth.logout')"
        @click="logout"
      />
    </div>
    <!-- USER CARD -->
    <div class="flex justify-center">
      <div
        class="bg-white rounded-[20px] border border-ternary max-w-[812px] flex-1 flex flex-col items-center gap-10 py-6 px-4 relative"
      >
        <!-- LOGOUT BUTTON -->
        <OutlineButton
          class="h-11 w-24 hidden md:block absolute top-4 right-4"
          :isLoading="isLoading('logout')"
          :btn-text="t('buttons.auth.logout')"
          @click="logout"
        />

        <!-- USER IMAGE WITH EMAIL -->
        <div class="flex flex-col gap-4 items-center">
          <img :src="userImage" class="size-[120px]" alt="user image" />
          <p class="text-muted text-xl leading-[24px] font-semibold">
            {{ userStore.user?.email ?? "fengshui@gmail.com" }}
          </p>
        </div>

        <!-- CURRENT USERS' SUBSCRIPTIONS -->
        <div class="flex flex-col gap-4 w-full sm:w-[480px]">
          <p class="bold-text">
            {{ t("subscription.plan-title") }}
          </p>
          <template
            v-for="premiumCalculator in ALL_PREMIUM_CALCULATORS"
            :key="premiumCalculator"
          >
            <ActiveSubscriptionCard
              v-if="userStore.userHasSubscription(premiumCalculator)"
              :subscription="userStore.user.subscriptions.find((s: any) => s.calculator === premiumCalculator)!"
              :isLoading="isLoading('cancel-subscription')"
              @cancelSubscription="handleCancelSubscription(premiumCalculator)"
            />
            <SubscriptionCard
              v-else
              :subscriptionName="premiumCalculator"
              @showSubscribeModal="openSubscriptionModal(premiumCalculator)"
            />
          </template>
        </div>
      </div>
    </div>
    <FamilyTable
      v-if="
        userStore.user !== null &&
        userStore.userHasSubscription(PREMIUM_CALCULATORS.activations)
      "
    />
    <DestinyMatrixPurchadesDatesTable
      v-if="
        userStore.user !== null &&
        !userStore.userHasSubscription(PREMIUM_CALCULATORS.destinyMatrix) &&
        userStore.user?.products['destiny_matrix']?.length > 0
      "
    />
    <AuthModal :show="showAuthModal" @close="handleCloseAuthModal" />
    <div v-if="showSubscribeModal">
      <SubscriptionModal
        :show="showSubscribeModal"
        :defaultSelected="defaultSelectedSubscription"
        :currentSubscriptions="userStore.user.subscriptions"
        @close="showSubscribeModal = false"
      />
    </div>
    <NotificationComponent />
  </div>
</template>

<style scoped>
.bold-text {
  @apply text-black font-semibold text-xl leading-[24px];
}
</style>
